import React, { useMemo } from 'react'
import { fi } from '../../utils/helpers'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import styled from '@emotion/styled'
import Button from '@mui/material/Button'
import { device, INTERNATIONAL_STREAM, OCR_STREAM } from '../../utils/constants'
import {
	bindPopover,
	bindTrigger,
	usePopupState
} from 'material-ui-popup-state/hooks'
import Popover from '@mui/material/Popover'
import { useMediaQuery } from '@mui/material'
import { useRecoilValue } from 'recoil'
import { sessionAtom } from '../../state/session'

const OCR_RELATED_SITES = [
	{
		id: 'activeResults',
		label: 'Active Results',
		redirectTo: 'https://activeresults-online.ocr.org.uk'
	},
	{
		id: 'examBuilder',
		label: 'ExamBuilder',
		redirectTo: 'https://exambuilder.ocr.org.uk/'
	},
	{
		id: 'interchange',
		label: 'Interchange',
		redirectTo: 'https://interchange.ocr.org.uk/'
	}
]

const INTERNATIONAL_RELATED_SITES = [
	{
		id: 'cambridgeAssessment',
		label: 'Cambridge Assessment International Education',
		redirectTo: 'https://www.cambridgeinternational.org/'
	},
	{
		id: 'cambridgeInternationalDirect',
		label: 'Cambridge International Direct',
		redirectTo: 'https://direct.cie.org.uk/login'
	},
	{
		id: 'submitForAssessment',
		label: 'Submit for Assessment',
		redirectTo: 'https://submitforassessment.cambridgeassessment.org.uk/'
	},
	{
		id: 'onlineLearnProDevelopment',
		label: 'Online Learning - professional development',
		redirectTo: 'https://learning.cambridgeinternational.org/'
	},
	{
		id: 'onlineLearnStudentSupport',
		label: 'Online Learning - student support',
		redirectTo: 'https://learning.cambridgeinternational.org/'
	}
]

const RELATED_SITES_MAP = new Map([
	[OCR_STREAM, OCR_RELATED_SITES],
	[INTERNATIONAL_STREAM, INTERNATIONAL_RELATED_SITES]
])

const ButtonElem = styled(Button)`
	.header-buttons {
		color: var(--color-white);
		font-size: 14px !important;
		padding: 0 !important;
		margin-left: 24px;
		font-family: var(--font-semi-bold) !important;
	}

	&:hover {
		background-color: unset !important;
	}

	@media ${device.tablet} {
		padding: 0 !important;
		color: var(--color-white);
	}
`

const DropdownElem = styled.div`
	background-color: var(--color-black);
	display: block;
	min-height: 103px;
	position: absolute;
	top: 43px;
	flex-direction: column;
	width: 200px;
	z-index: 9999;

	a {
		&:first-of-type {
			margin-top: 24px;
		}

		&:last-of-type {
			margin-bottom: 24px;
		}

		margin-top: 8px;
		display: flex;
		align-items: center;
		margin-left: 24px;
		font-size: 14px;
		font-family: var(--font-semi-bold);
	}

	@media ${device.tablet} {
		display: flex;
		position: unset;
	}
`

const PopoverContent = styled.div`
	display: flex;
	flex-direction: column;
	background-color: var(--color-black);
	min-height: 103px;
	width: 200px;
	padding: 12px;

	a {
		color: var(--color-white);
		margin-top: 8px;
		display: flex;
		align-items: center;
		font-size: 14px;
		font-family: var(--font-semi-bold);
	}

	@media ${device.tablet} {
		width: 100%;
	}
`
const RelatedSites = () => {
	const userSession = useRecoilValue(sessionAtom)
	const userSelectedStream = userSession?.selectedBusinessStream || OCR_STREAM

	const relatedSites = RELATED_SITES_MAP.get(userSelectedStream) || OCR_RELATED_SITES
	const dropdownTitle = userSelectedStream === INTERNATIONAL_STREAM ? 'Our other sites' : 'Teach Cambridge'

	const popupState = usePopupState({
		variant: 'popover',
		popupId: 'related-sites'
	})
	const mobile = useMediaQuery(device.mobile)

	const popoverPosition = useMemo(() => {
		if (mobile) {
			return {
				anchor: { vertical: 'top', horizontal: 'center' },
				transform: { vertical: 'bottom', horizontal: 'center' }
			}
		}
		return {
			anchor: { vertical: 'bottom', horizontal: 'left' },
			transform: { vertical: 'top', horizontal: 'left' }
		}
	}, [mobile])

	return (
		<div data-testid="related-sites-section">
			<ButtonElem
				data-testid="related-sites-button"
				disableRipple
				className="header-buttons"
				style={{ fontSize: '14px', fontWeight: 400 }}
				{...bindTrigger(popupState)}
			>
				{dropdownTitle}
				{fi(
					!popupState.isOpen,
					<ExpandMore fontSize="small" />,
					<ExpandLess fontSize="small" />
				)}
			</ButtonElem>

			<Popover
				{...bindPopover(popupState)}
				anchorOrigin={popoverPosition.anchor as any}
				transformOrigin={popoverPosition.transform as any}
				data-testid="related-sites-dropdown"
			>
				<PopoverContent>
					{relatedSites.map((item, index) => (
						<a
							href={item.redirectTo}
							target="_blank"
							key={item.label}
							data-testid={`${item.id}-test-id`}
						>
							{item.label}
						</a>
					))}
				</PopoverContent>
			</Popover>
		</div>
	)
}

export default RelatedSites
